export const CONVERSION_PREFIX_SET = new Set([
    'conversions',
    'conversionRate',
    'conversionValues',
    'conversionValue',
    'cpa',
    'roas',
    'cpaWithTotalSpend',
]);

/**
 * Step 1: Split Metric into Cell
 * @description
 * split based on delimiters (: and __)
 * Exceptions: inside parentheses and url(://) are ignored
 */
function splitMetric(metric) {
    const str = metric.replace(/__/g, ':');

    let result = [];
    let buffer = '';
    let bracketCount = 0;
    let i = 0;

    while (i < str.length) {
        if (str[i] === '(') bracketCount++;
        if (str[i] === ')') bracketCount = Math.max(0, bracketCount - 1);

        // ignore : inside parentheses
        if (str[i] === ':' && bracketCount === 0) {
            const next = str.slice(i + 1);

            // ignore : inside url
            if (next.match(/^(\/\/|\s)/)) {
                buffer += str[i];
            } else {
                result.push(buffer);
                buffer = '';
            }
        } else {
            buffer += str[i];
        }

        i++;
    }

    if (buffer) result.push(buffer);

    return result;
}

/**
 * Step 2.1: Add space
 * @description
 * - add space between lowercase and uppercase => camelCase
 * - replace (- and _ and .) with space => kebab-case & snake_case & dot.case
 * - add space before (
 */
function replaceWithSpace(cell) {
    return cell.replace(/([a-z])([A-Z])|[-_.(]/g, (match, p1, p2) => {
        // camelCase
        if (p1 && p2) return `${p1} ${p2}`;

        if (match === '(') return ' (';

        // kebab-case & snake_case & dot.case
        return ' ';
    });
}
/**
 * Step 2.2: Convert to uppercase
 * @description
 * - convert first letter to uppercase
 * - convert lowercase letters after ' ', '(', ')', ',', '/ to uppercase
 */
function toUppercase(cell) {
    return cell.replace(/(?:^|[ (),/])([a-z])/g, (match) => {
        return match.toUpperCase();
    });
}
/**
 * Step 2.3: Normalize spaces
 * - multiple spaces to a single space
 * - trim spaces at the start/end of text and inside parentheses
 */
function normalizeSpaces(cell) {
    return cell
        .replace(/\s+/g, ' ')
        .replace(/^\s+|\s+$|(\(\s+|\s+\))/g, (match) => {
            return match.trim();
        });
}

/**
 * Step 2: Refine a Cell
 */
function refineMetricCell(cell) {
    const spaced = replaceWithSpace(cell || '');
    const uppered = toUppercase(spaced);
    const normalized = normalizeSpaces(uppered);
    return normalized;
}

/**
 * Step 3: Join Cells with ': '
 */
function joinCells(cells) {
    return cells.filter((cell) => !!cell).join(': ');
}

/**
 * Helper: Get cell locale with multi-namespace support
 * Searches 'channel' first, then 'metrics' (a copy of the server file, which
 * absorbed fixedMetric). Field names are settled in metrics (D6/D7), so
 * 'connector' left the chain (WP1a).
 * Falls back to refineMetricCell if not found
 * This ensures identical behavior with React's getFieldLocaleWith
 */
const getCellLocale = function (cell) {
    // Try channel namespace
    if (this.$te(`CHANNEL.${cell}`)) {
        return this.$t(`CHANNEL.${cell}`);
    }

    // Try metrics namespace
    if (this.$te(`METRICS.${cell}`)) {
        return this.$t(`METRICS.${cell}`);
    }

    // Fallback to refined cell
    return refineMetricCell(cell);
};

export const getFieldLocale = function (_name) {
    const name = _name + '';

    if (!name) return '';

    const cells = splitMetric(name);
    const [headCell, ...restCells] = cells;

    /**
     * 1. GSS 인 경우 헤더 셀만 변환 후 조합
     */
    if (headCell === 'gss') {
        const refinedHeadCell = getCellLocale.call(this, headCell);
        return joinCells([refinedHeadCell, ...restCells]);
    }

    /**
     * 2. Conversion 인 경우 셀 변환 후 순서 변경 후 조합
     */
    if (CONVERSION_PREFIX_SET.has(headCell)) {
        const [type, channel] = name.split(':');
        const parts = [type, channel, name.replace(`${type}:${channel}:`, '')];
        const [typeLocale, channelLocale, metricLocale] = parts.map(
            getCellLocale.bind(this)
        );

        if (type === 'conversions') {
            return joinCells([channelLocale, metricLocale]);
        }

        return joinCells([channelLocale, metricLocale, typeLocale]);
    }

    /**
     * 3. 나머지 경우 모든 셀 변환 후 조합
     */
    const refinedCells = cells.map(getCellLocale.bind(this));
    return joinCells(refinedCells);
};
